import { useMutation, useQuery } from 'react-query'
import { TelemedicineServices } from '../../services/TelemedicineServices'
import { IQueryParams } from '@/types'
import { useNavigate } from 'react-router-dom'
import { CreateAppointmentDataDomain } from '../../services/mapper/AppointmentRequestMapper/CreateAppointmentMapper/CreateAppointmentMapper.types'
import { toast } from '@conexasaude/conexa-hero'
import { useModalStore } from '@/store/useModal'
import useScheduleAppointmentStore from '../../store/ScheduleAppointment/ScheduleAppointment'
import { DataFormatted } from '../../store/ScheduleAppointment/ScheduleAppointment.types'
import { INITIAL_DATA_FORMATTED_DATA } from '../../store/ScheduleAppointment/ScheduleAppointment.constants'
import { UpdaterContactPayload } from '../../services/TelemedicineServices.types'
import {
  PsyAliveListParams,
  UsePsyScheduleParams,
} from '../../services/mapper/PsyAliveListMapper'
import {
  ProfessionalScheduleParams,
  ProfessionalsParams,
} from '../../services/mapper/ProfessionalsMapper'

export const useGetClinicList = (clinicName: string) => {
  const { data } = useQuery(
    [`clinicList`, clinicName],
    () => TelemedicineServices.getClinicList(clinicName),
    {
      enabled: Boolean(clinicName),
    },
  )

  return {
    clinicList: data,
  }
}

export const useGetPatientList = (queryParams?: IQueryParams) => {
  const { data } = useQuery(
    [`patientList-${JSON.stringify(queryParams)}`],
    () => TelemedicineServices.getPatientList(queryParams),
    {
      enabled:
        String(queryParams?.term)?.length > 0 && Boolean(queryParams?.clinicId),
    },
  )

  return {
    patientList: data,
  }
}

export const useGetMedicalRecordPatientList = (queryParams?: IQueryParams) => {
  const { data } = useQuery(
    [`patientList-${JSON.stringify(queryParams)}`],
    () => TelemedicineServices.getMedicalRecordPatientList(queryParams),
    {
      enabled:
        String(queryParams?.term)?.length > 0 && Boolean(queryParams?.clinicId),
    },
  )
  return {
    patientList: data,
  }
}

export const useGetObjectiveAppointmentList = (queryParams: IQueryParams) => {
  const { data } = useQuery(
    [`objectiveAppointmentList-${JSON.stringify(queryParams)}`],
    () => TelemedicineServices.getObjectiveAppointmentList(queryParams),
    {
      enabled: Boolean(queryParams?.clinicId),
    },
  )

  return {
    objectiveAppointmentList: data,
  }
}

export const useGetPsychologistSearchFilters = (isPsychologist: boolean) => {
  const { data } = useQuery(
    ['searchPsychologistFilters'],
    () => TelemedicineServices.getPsychologistSearchFilters(),
    {
      enabled: isPsychologist,
    },
  )

  return {
    searchPsychologistFilters: data,
  }
}

export const useGetClinicConfig = (clinicId: number) => {
  const { data, refetch } = useQuery(
    ['clinicConfig'],
    () => TelemedicineServices.getClinicConfig(clinicId),
    {
      enabled: false,
    },
  )

  return {
    clinicConfig: data,
    clinicConfigFetch: refetch,
  }
}

export const useGetProfessionalTypeList = (queryParams?: IQueryParams) => {
  const { data } = useQuery(
    [`professionalTypeList-${JSON.stringify(queryParams)}`],
    () => TelemedicineServices.getProfessionalTypeList(queryParams),
    {
      enabled: Boolean(queryParams?.clinicId),
    },
  )

  return {
    professionalTypeList: data,
  }
}

export const useGetProfessionalTypeListByHealthSupport = (
  queryParams?: IQueryParams,
) => {
  const { data } = useQuery(
    [`professionalTypeList-${JSON.stringify(queryParams)}`],
    () =>
      TelemedicineServices.getProfessionalTypeListByHealthSupport(queryParams),
    {
      enabled: Boolean(queryParams?.clinicId),
    },
  )

  return {
    professionalTypeList: data,
  }
}

export const useGetProfessionalList = (queryParams?: IQueryParams) => {
  const { data } = useQuery(
    [`professionalList-${JSON.stringify(queryParams)}`],
    () => TelemedicineServices.getProfessionalList(queryParams),
    {
      enabled: Boolean(queryParams?.clinicId),
    },
  )

  return {
    professionalList: data,
  }
}

export const useGetSpecialtyList = (queryParams?: IQueryParams) => {
  const { data } = useQuery(
    [`specialtyList-${JSON.stringify(queryParams)}`],
    () => TelemedicineServices.getSpecialtyList(queryParams),
    {
      enabled: Boolean(queryParams?.patientId),
    },
  )

  return {
    specialtyList: data,
  }
}

export const useSubmitScheduleAppointment = () => {
  const navigate = useNavigate()
  const { updateValue } = useScheduleAppointmentStore()

  const mutation = useMutation(
    (queryParams?: DataFormatted) =>
      TelemedicineServices.submitScheduleAppointment(queryParams),
    {
      onSuccess: data => {
        const newValue = {
          data: data?.object?.data,
          total: data?.object?.total,
        }

        updateValue('scheduleList', newValue)
        navigate('/atendimento/agendar/consulta/profissionais')
      },
    },
  )

  return {
    submitScheduleAppointment: mutation,
    submitScheduleAppointmentIsLoading: mutation.isLoading,
  }
}

export const useSubmitScheduleAppointmentByInfiniteScroll = () => {
  const navigate = useNavigate()
  const { scheduleList, updateValue } = useScheduleAppointmentStore()

  const mutation = useMutation(
    (queryParams?: DataFormatted) =>
      TelemedicineServices.submitScheduleAppointment(queryParams),
    {
      onSuccess: data => {
        const newValue = {
          data: [...(scheduleList?.data || []), ...(data?.object?.data || [])],
          total: data?.object?.total,
        }

        updateValue('scheduleList', newValue)
        navigate('/atendimento/agendar/consulta/profissionais')
      },
    },
  )

  return {
    submitScheduleAppointmentByInfiniteScroll: mutation,
    submitScheduleAppointmentByInfiniteScrollIsLoading: mutation.isLoading,
  }
}

export const useUpdaterContact = () => {
  const mutation = useMutation(({ data, patientId }: UpdaterContactPayload) =>
    TelemedicineServices.updaterContact({
      patientId,
      data,
    }),
  )

  return {
    updaterContact: mutation,
    updaterContactIsLoading: mutation.isLoading,
  }
}

export const useCreateAppointment = () => {
  const navigate = useNavigate()
  const closeModal = useModalStore(state => state.closeModal)
  const { updateValue } = useScheduleAppointmentStore()

  const mutation = useMutation(
    (data: CreateAppointmentDataDomain) =>
      TelemedicineServices.createAppointment(data),
    {
      onSuccess: () => {
        closeModal()
        navigate('/atendimento/agendar/consulta')
        toast(
          'Atendimento agendado! Paciente também recebeu a confirmação do agendamento.',
        )
        updateValue('dataFormatted', INITIAL_DATA_FORMATTED_DATA)
        updateValue('persistDataForm', false)
        updateValue('fittingAppointment', false)
      },
    },
  )

  return {
    createAppointment: mutation,
    createAppointmentIsLoading: mutation.isLoading,
  }
}

export const useGetHealthInsuranceList = (queryParams?: IQueryParams) => {
  const { data } = useQuery(
    [`healthInsuranceList-${JSON.stringify(queryParams)}`],
    () => TelemedicineServices.getHealthInsuranceList(queryParams),
    {
      enabled: Boolean(queryParams?.patientId),
    },
  )

  return {
    healthInsuranceList: data,
  }
}

export const useGetResponsibleList = () => {
  const { data } = useQuery(
    ['responsibleList'],
    TelemedicineServices.getResponsibleList,
  )

  return {
    responsibleList: data,
  }
}

export const useGetPsyAliveList = (
  clinicId: number,
  params: PsyAliveListParams,
) => {
  const { data, isLoading } = useQuery(
    ['psy-alive-List', clinicId, params],
    () => TelemedicineServices.getPsyAliveList(clinicId, params),
    {
      enabled: !!clinicId,
    },
  )

  return {
    data,
    isLoading,
  }
}

export const useGetProfessinals = (
  clinicId: number,
  params: ProfessionalsParams,
) => {
  const { data, isLoading } = useQuery(
    ['professionals', clinicId, params],
    () => TelemedicineServices.getProfessionals(clinicId, params),
    {
      enabled: !!clinicId,
    },
  )

  return {
    data,
    isLoading,
  }
}

export const usePsySchedule = (params: UsePsyScheduleParams) => {
  return useQuery({
    queryKey: [
      'get-psy-schedule-v2',
      params?.professionalId,
      params?.startDate,
      params?.endDate,
    ],
    queryFn: () => TelemedicineServices.getPsychologistSchedule(params),
    refetchOnWindowFocus: false,
    retry: 1,
    enabled:
      !!params?.startDate && !!params?.endDate && !!params?.professionalId,
  })
}

export const useProfessionalSchedule = (params: ProfessionalScheduleParams) => {
  return useQuery({
    queryKey: [
      'get-professional-schedule',
      params?.professionalId,
      params?.startDate,
      params?.endDate,
    ],
    queryFn: () => TelemedicineServices.getProfessionalSchedule(params),
    refetchOnWindowFocus: false,
    retry: 1,
    enabled:
      !!params?.startDate && !!params?.endDate && !!params?.professionalId,
  })
}
